import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus, faFile, faSpinner,  faFileAlt, faCheckCircle, faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, BarElement } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { dashboardAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState({
    totalProjects: 0,
    totalDocuments: 0,
    documentsAnalyzed: 0,
    analysisStatus: {
      completed: 1, // Default to 1 to show the donut chart with just green
      pending: 0,
      failed: 0
    },
    projectDocumentCounts: [],
    timelineData: []
  });

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all dashboard data from the API endpoint
        const dashboardResponse = await dashboardAPI.getDashboardData();
        console.log('Dashboard API response:', dashboardResponse);
        
        // The API returns data in a nested 'data' property
        const dashboardData = dashboardResponse.data.data;
        
        if (dashboardData) {
          // console.log('Dashboard data:', dashboardData);
          // Set projects
          setProjects(dashboardData.projects || []);
          
          // Set recent documents
          setRecentDocuments(dashboardData.recent_documents || []);
          
          // Set dashboard stats
          setDashboardStats({
            totalProjects: dashboardData.total_projects || 0,
            totalDocuments: dashboardData.total_documents || 0,
            documentsAnalyzed: dashboardData.documents_analyzed || 0,
            analysisStatus: dashboardData.analysis_status || {
              completed: 1, // Default to 1 to show the chart
              pending: 0,
              failed: 0
            },
            projectDocumentCounts: dashboardData.project_document_counts || [],
            timelineData: dashboardData.timeline_data || [],
            tags: dashboardData.tags || []
          });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Handle loading state
  if (loading) {
    return (
      <Container className="py-4">
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-primary mb-3" />
          <p>Loading dashboard data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container  className="py-4 bg-light">
      {error && <Alert variant="danger">{error}</Alert>}
      
      {/* Metrics Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3">
                <FontAwesomeIcon icon={faFolderOpen} className="text-primary" size="lg" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Total Projects</h6>
                <h3 className="mb-0">{dashboardStats.totalProjects}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-info bg-opacity-10 me-3">
                <FontAwesomeIcon icon={faFileAlt} className="text-info" size="lg" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Total Documents</h6>
                <h3 className="mb-0">{dashboardStats.totalDocuments}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4}>
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="lg" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Documents Analyzed</h6>
                <h3 className="mb-0">{dashboardStats.documentsAnalyzed}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Charts Row */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom-0">
              <h6 className="mb-0">Documents per Project</h6>
            </Card.Header>
            <Card.Body>
              {projects.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No project data available</p>
                </div>
              ) : (
                <div style={{ height: '250px' }}>
                  <Bar 
                    data={{
                      labels: dashboardStats.projectDocumentCounts?.map(p => p.project_name) || [],
                      datasets: [{
                        label: 'Documents',
                        data: dashboardStats.projectDocumentCounts?.map(p => p.document_count) || [],
                        backgroundColor: '#4285F4',
                        borderRadius: 4
                      }]
                    }} 
                    options={chartOptions} 
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom-0">
              <h6 className="mb-0">Document Analysis Timeline</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '250px' }}>
                <Line 
                  data={{
                    labels: dashboardStats.timelineData?.map(item => item.date) || [],
                    datasets: [
                      {
                        label: 'Uploaded',
                        data: dashboardStats.timelineData?.map(item => item.uploaded) || [],
                        borderColor: '#4285F4',
                        backgroundColor: 'rgba(66, 133, 244, 0.1)',
                        tension: 0.4,
                        fill: true
                      },
                      {
                        label: 'Analyzed',
                        data: dashboardStats.timelineData?.map(item => item.analyzed) || [],
                        borderColor: '#34A853',
                        backgroundColor: 'rgba(52, 168, 83, 0.1)',
                        tension: 0.4,
                        fill: true
                      }
                    ]
                  }} 
                  options={chartOptions} 
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Analysis Status and Recent Projects */}
      <Row className="mb-4">
        <Col md={5}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom-0">
              <h6 className="mb-0">Analysis Status</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '250px' }} className="d-flex justify-content-center">
                <div style={{ width: '200px', height: '200px' }}>
                  <Bar 
                    data={{
                      labels: ['Failed', 'Pending', 'Completed'],
                      datasets: [{
                        data: [
                          dashboardStats.analysisStatus.failed || 0,
                          dashboardStats.analysisStatus.pending || 0,
                          dashboardStats.analysisStatus.completed || 0
                        ],
                        backgroundColor: ['#EA4335', '#FBBC05', '#34A853'],
                        borderWidth: 0,
                        borderRadius: 4,
                        maxBarThickness: 40
                      }]
                    }} 
                    options={{
                      ...chartOptions,
                      indexAxis: 'y',
                      plugins: {
                        legend: {
                          display: false
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `${context.raw} documents`;
                            }
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: {
                            display: false
                          }
                        },
                        x: {
                          grid: {
                            display: false
                          }
                        }
                      }
                    }} 
                  />
                </div>
              </div>
              <div className="d-flex justify-content-center mt-3">
                <div className="d-flex align-items-center me-3">
                  <div className="rounded-circle me-2" style={{ width: '10px', height: '10px', backgroundColor: '#EA4335' }}></div>
                  <small>Failed</small>
                </div>
                <div className="d-flex align-items-center me-3">
                  <div className="rounded-circle me-2" style={{ width: '10px', height: '10px', backgroundColor: '#FBBC05' }}></div>
                  <small>Pending</small>
                </div>
                <div className="d-flex align-items-center">
                  <div className="rounded-circle me-2" style={{ width: '10px', height: '10px', backgroundColor: '#34A853' }}></div>
                  <small>Completed</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={7}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom-0">
              <h6 className="mb-0">Recent Projects</h6>
            </Card.Header>
            <Card.Body>
              {projects.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No projects found</p>
                  <Button as={Link} to="/projects/create" variant="primary" size="sm">
                    <FontAwesomeIcon icon={faFolderPlus} className="me-2" />
                    Create Project
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="mb-3">
                    {projects.slice(0, 3).map(project => (
                      <div key={project.id} className="mb-3 p-2 border-bottom">
                        <h6 className="mb-1">{project.name}</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Created {new Date(project.created_at || Date.now()).toLocaleDateString()}</small>
                          <Button 
                            as={Link} 
                            to={`/projects/${project.id}`} 
                            variant="outline-primary" 
                            size="sm"
                          >
                            View Documents
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="text-center mt-3">
                    <Button as={Link} to="/projects" variant="link" size="sm">
                      View All Projects
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Recent Documents Table */}
      <Row>
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom-0">
              <h6 className="mb-0">Recent Documents</h6>
            </Card.Header>
            <Card.Body className="p-0">
              {recentDocuments.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No documents found</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr className="bg-light">
                      <th className="ps-3">Document</th>
                      <th>Project</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDocuments.map(document => (
                      <tr key={document.id}>
                        <td className="ps-3">
                          <Link to={`/documents/${document.id}`} className="text-decoration-none">
                            <FontAwesomeIcon icon={faFile} className="me-2 text-info" />
                            {document.name || document.name || 'Untitled Document'}
                          </Link>
                        </td>
                        <td>
                          {document.project_name ? (
                            <Link to={`/projects/${document.project_id}`} className="text-decoration-none">
                              {document.project_name}
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          <Badge 
                            bg={document.analyzed === 1 ? 'success' : 
                               document.analyzed === 0 ? 'warning' : 'danger'}
                            style={{ fontWeight: 'normal', padding: '0.35em 0.65em' }}
                          >
                            {document.analyzed === 1 ? 'Analyzed' : 'Not Analyzed'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
